import Config from '@config';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ConnectionApplication,
  ConnectionApplicationDetail,
  CreateConnectionApplicationParams,
  CreateConnectionApplicationResult,
  DocumentRequirement,
  GetDocumentRequirementsParams,
  RejectedDocument,
  ResubmitDocumentsParams,
  ResubmitDocumentsResult,
  GetConnectionApplicationsParams,
  SendConnectionApplicationMessageParams,
  SendConnectionApplicationMessageResult,
} from '@/features/connectionApplications/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

const appendDocuments = (formData: FormData, documents?: Record<string, File>) =>
  Object.entries(documents ?? {}).forEach(([id, file]) => formData.append(`documents[${id}]`, file));

export const connectionApplicationsApi = createApi({
  reducerPath: 'connectionApplicationsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/connection-applications',
  }),
  tagTypes: ['ConnectionApplication', 'ConnectionApplicationDetail'],
  endpoints: builder => ({
    getConnectionApplications: builder.query<ConnectionApplication[], GetConnectionApplicationsParams>({
      query: params => ({
        url: '',
        params,
      }),
      providesTags: ['ConnectionApplication'],
    }),
    getConnectionApplicationById: builder.query<ConnectionApplicationDetail, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'ConnectionApplicationDetail', id }],
    }),
    createConnectionApplication: builder.mutation<
      CreateConnectionApplicationResult,
      CreateConnectionApplicationParams
    >({
      query: ({ documents, ...fields }) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) formData.append(key, String(value));
        });
        appendDocuments(formData, documents);

        return {
          url: '',
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['ConnectionApplication'],
    }),
    getDocumentRequirements: builder.query<DocumentRequirement[], GetDocumentRequirementsParams>({
      query: params => ({ url: '/document-requirements', params }),
    }),
    getRejectedDocuments: builder.query<RejectedDocument[], string>({
      query: id => ({ url: `/${id}/rejected-documents` }),
      providesTags: (_result, _error, id) => [{ type: 'ConnectionApplicationDetail', id }],
    }),
    resubmitDocuments: builder.mutation<ResubmitDocumentsResult, ResubmitDocumentsParams>({
      query: ({ connectionApplicationId, documents }) => {
        const formData = new FormData();
        appendDocuments(formData, documents);

        return {
          url: `/${connectionApplicationId}/resubmit-documents`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: (_result, _error, { connectionApplicationId }) => [
        'ConnectionApplication',
        { type: 'ConnectionApplicationDetail', id: connectionApplicationId },
      ],
    }),
    sendConnectionApplicationMessage: builder.mutation<
      SendConnectionApplicationMessageResult,
      SendConnectionApplicationMessageParams
    >({
      query: ({ connectionApplicationId, body, files }) => {
        const formData = new FormData();
        formData.append('body', body);
        files?.forEach(file => formData.append('files', file));

        return {
          url: `/${connectionApplicationId}/messages`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: (_result, _error, { connectionApplicationId }) => [
        'ConnectionApplication',
        { type: 'ConnectionApplicationDetail', id: connectionApplicationId },
      ],
    }),
    markConnectionApplicationRead: builder.mutation<void, string>({
      query: id => ({ url: `/${id}/read`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        'ConnectionApplication',
        { type: 'ConnectionApplicationDetail', id },
      ],
    }),
  }),
});

export const {
  useGetConnectionApplicationsQuery,
  useGetConnectionApplicationByIdQuery,
  useCreateConnectionApplicationMutation,
  useGetDocumentRequirementsQuery,
  useGetRejectedDocumentsQuery,
  useResubmitDocumentsMutation,
  useSendConnectionApplicationMessageMutation,
  useMarkConnectionApplicationReadMutation,
} = connectionApplicationsApi;
